import React, { useState, useEffect } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  IconButton,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";

const FilterMenu = ({
  filterOptions,
  selectedFilter,
  onFilterChange,
  iconSize = "md",
  showIcon = true,
  multiple = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  /* ---------- helpers ---------- */
  const totalItems = filterOptions.filter((o) => o.pref !== "all").length;

  const isItemSelected = (item) =>
    multiple
      ? selectedFilter.includes("all") || selectedFilter.includes(item.pref)
      : selectedFilter === "all" || selectedFilter === item.pref;

  const getButtonText = () => {
    if (!multiple) {
      return selectedFilter === "none"
        ? "Select All"
        : filterOptions.find((i) => i.pref === selectedFilter)?.label ||
            "Select All";
    }
    if (
      selectedFilter.length === 0 ||
      (selectedFilter.length === 1 && selectedFilter[0] === "all")
    )
      return "Select All";
    if (selectedFilter.length === totalItems) return "Select All";
    return `${selectedFilter.length} selected`;
  };

  /* ---------- main click handler ---------- */
  const handleFilterClick = (e, item) => {
    e.preventDefault();

    /* SINGLE-SELECT mode (unchanged) */
    if (!multiple) {
      if (item.pref === "all") {
        onFilterChange(selectedFilter === "all" ? "none" : "all");
      } else {
        onFilterChange(item.pref);
      }
      return;
    }

    /* MULTI-SELECT mode */
    const isAllSelected = selectedFilter.includes("all");

    if (item.pref === "all") {
      /* toggle the master switch */
      onFilterChange(isAllSelected ? [] : ["all"]);
      return;
    }

    if (isAllSelected) {
      /* “all” is on → untick only the clicked item, keep the rest */
      const newSelection = filterOptions
        .filter((opt) => opt.pref !== "all" && opt.pref !== item.pref)
        .map((opt) => opt.pref);
      onFilterChange(newSelection);
      return;
    }

    /* normal individual toggle */
    let newSelection = selectedFilter.includes(item.pref)
      ? selectedFilter.filter((f) => f !== item.pref) // remove
      : [...selectedFilter, item.pref]; // add

    /* 0 items → fallback to “none” (or keep [] if you prefer) */
    if (newSelection.length === 0) {
      onFilterChange([]);
      return;
    }

    /* all individual items selected → collapse to ["all"] */
    if (newSelection.length === totalItems) {
      onFilterChange(["all"]);
      return;
    }

    onFilterChange(newSelection);
  };

  /* ---------- render ---------- */
  return (
    <Menu closeOnSelect={false} autoSelect={false} onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)}>
      <MenuButton
        as={IconButton}
        icon={showIcon ? <ChevronDownIcon /> : null}
        size={iconSize}
        variant="ghost"
        _hover="none"
        _active="none"
        aria-label="Filter options"
      >
        {getButtonText()}
      </MenuButton>

      <MenuList>
        {filterOptions.map((item) => (
          <MenuItem
            key={item.pref}
            onClick={(e) => handleFilterClick(e, item)}
            icon={<Checkbox isChecked={isItemSelected(item)} colorScheme="customGreen" />}
          >
            {item.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default FilterMenu;
